import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

const AuthContext = createContext(null);

/* localStorage keys */
const LS_USER    = 'pg_user';
const LS_STATION = 'pg_station_id';   // currently-selected station id

/**
 * AuthProvider — carries:
 *   - user: { id, name, email, ..., role_name, is_admin, permissions,
 *             is_noris_team, base_station_id }
 *     permissions is either '*' (admin) or { module: {create, read, update, delete, approve} }
 *   - currentStationId: number — id of the station currently in scope
 *   - setCurrentStation(id): switch the active station; persists to localStorage
 *   - can(module, action): boolean helper
 *
 * Multi-station rules:
 *   • On login, currentStationId defaults to the user's base_station_id
 *   • The chosen id is persisted in localStorage so reload preserves it
 *   • When the user changes, currentStationId is reset to their base
 *   • api.js reads the same localStorage key and sends X-Station-Id on every request
 */
export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStationId, setStation] = useState(null);

  /* ── Restore from localStorage on mount ── */
  useEffect(() => {
    const saved = localStorage.getItem(LS_USER);
    if (saved) {
      try {
        const u = JSON.parse(saved);
        setUser(u);
        const stored = parseInt(localStorage.getItem(LS_STATION), 10);
        // Default to base_station_id if nothing stored or stored value is invalid
        const fallback = u?.base_station_id || null;
        setStation(Number.isFinite(stored) && stored > 0 ? stored : fallback);
      } catch (_) {
        localStorage.removeItem(LS_USER);
        localStorage.removeItem(LS_STATION);
      }
    }
    setLoading(false);
  }, []);

  /* ── login / logout ── */
  const login = useCallback((userData) => {
    localStorage.setItem(LS_USER, JSON.stringify(userData));
    setUser(userData);
    // On fresh login, always start at the base station.
    const baseId = userData?.base_station_id || null;
    if (baseId) {
      localStorage.setItem(LS_STATION, String(baseId));
      setStation(baseId);
    } else {
      localStorage.removeItem(LS_STATION);
      setStation(null);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(LS_USER);
    localStorage.removeItem(LS_STATION);
    setUser(null);
    setStation(null);
  }, []);

  /* ── Live profile/permission refresh ──
   * Re-fetch /me and merge fresh permissions into the cached user so changes
   * an admin makes to a role (e.g. granting Day-End Reconciliation) take
   * effect without forcing the affected user to log out. /me does NOT return
   * the auth token, so we preserve the existing one. */
  const refreshUser = useCallback(async () => {
    if (!localStorage.getItem(LS_USER)) return;
    try {
      const fresh = await api.auth.me();
      if (!fresh) return;
      setUser(prev => {
        if (!prev) return prev;
        const merged = { ...prev, ...fresh, token: prev.token };
        localStorage.setItem(LS_USER, JSON.stringify(merged));
        return merged;
      });
    } catch (_) { /* offline / transient — keep the cached user */ }
  }, []);

  /* Sync on initial load and whenever the tab regains focus, so permission
   * grants/revokes propagate on the next navigation without a re-login. */
  useEffect(() => {
    refreshUser();
    const onFocus = () => refreshUser();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshUser]);

  /* ── Switch the active station ── */
  const setCurrentStation = useCallback((id) => {
    const sid = parseInt(id, 10);
    if (!Number.isFinite(sid) || sid <= 0) return;
    localStorage.setItem(LS_STATION, String(sid));
    setStation(sid);
    // Notify the rest of the app so caches invalidate / pages refetch.
    // Components can listen via window.addEventListener('pg-station-change', ...)
    window.dispatchEvent(new CustomEvent('pg-station-change', { detail: { stationId: sid } }));
  }, []);

  /* ── Permission helpers (unchanged) ── */
  const can = (module, action) => {
    if (!user) return false;
    if (user.is_admin || user.permissions === '*') return true;
    const p = user.permissions || {};
    return !!(p[module] && p[module][action]);
  };

  const canAccess = (module) => {
    if (!user) return false;
    if (user.is_admin || user.permissions === '*') return true;
    const p = user.permissions || {};
    if (!p[module]) return false;
    return ['create','read','update','delete','approve'].some(a => p[module][a]);
  };

  /* ── Trial calc (unchanged) ── */
  const today = new Date();
  today.setHours(0,0,0,0);
  const end = user?.trial_end_date ? new Date(user.trial_end_date) : null;
  if (end) end.setHours(0,0,0,0);
  const isTrialExpired = end ? (end < today) : false;
  const daysRemaining  = end ? Math.ceil((end - today) / (1000 * 60 * 60 * 24)) : null;

  /* ── Convenience flag for Noris team ── */
  const isNorisTeam = !!user?.is_noris_team;

  return (
    <AuthContext.Provider value={{
      user, login, logout, loading, refreshUser,
      currentStationId, setCurrentStation,
      can, canAccess,
      isTrialExpired, daysRemaining,
      isNorisTeam,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
